// dslChartAdapter.ts — DSL interpret 결과 → lightweight-charts v5 (visuals 6종 렌더)
// 2층 구조: ① toChartSpec = 순수 변환(차트 비의존 — vitest 전건 검증 대상)
//           ② attachDslIndicator = chart에 실제 부착(색 해석·시리즈 생성 — 배선 화면에서 실렌더 검증)
// 색은 슬롯명/hex 원문을 스펙에 유지하고 부착 시점에 resolveColor(readColors 기반)로 해석
//   — oklch iOS canvas 함정(PriceChart readColors 주석)과 동일한 이유.
// ⚠ v1 렌더 폴백 (갭 리포트 2026-07-11-dsl-v1-template-gallery-gap-report.md):
//   band/fill의 면 채움은 lightweight-charts 기본 미지원 → 점선 라인 2개 폴백(기존 볼린저 관례와 동일).
import {
  AreaSeries,
  BaselineSeries,
  HistogramSeries,
  LineSeries,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type SeriesMarker,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { IndicatorDoc } from '@core/sim/indicators/dsl/schema';
import type { InterpretResult } from '@core/sim/indicators/dsl/interpret';

export type DslLinePoint = { time: UTCTimestamp; value: number } | { time: UTCTimestamp };
export type DslLineSpec = { key: string; points: DslLinePoint[]; color: string; width: 1 | 2 | 3; dashed: boolean; style?: 'solid' | 'dashed' | 'dotted'; opacity?: number };
export type DslPriceLineSpec = { price: number; color: string; label?: string };
export type DslMarkerSpec = { time: UTCTimestamp; position: 'aboveBar' | 'belowBar'; shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square'; color: string; text?: string };
export type DslBackgroundSpec = { time: UTCTimestamp; color: string; opacity: number };
export type DslHistogramPoint = { time: UTCTimestamp; value: number; color: string } | { time: UTCTimestamp };
export type DslHistogramSpec = { key: string; points: DslHistogramPoint[]; base: number; opacity?: number };
export type DslAreaSpec = { key: string; points: DslLinePoint[]; color: string; opacity: number; seriesOpacity?: number };
export type DslBaselineSpec = { key: string; points: DslLinePoint[]; base: number; colorUp: string; colorDown: string; opacity?: number };
export type DslChartSpec = { lines: DslLineSpec[]; priceLines: DslPriceLineSpec[]; markers: DslMarkerSpec[]; backgrounds: DslBackgroundSpec[]; histograms?: DslHistogramSpec[]; areas?: DslAreaSpec[]; baselines?: DslBaselineSpec[] };

// null 구간은 whitespace 포인트({time}만) — 시리즈별 시작 time이 어긋나면 렌더가 깨짐(PriceChart toLine과 동일)
function toPoints(times: number[], vals: (number | null)[]): DslLinePoint[] {
  return vals.map((v, i) => (v != null ? { time: times[i] as UTCTimestamp, value: v } : { time: times[i] as UTCTimestamp }));
}

/** interpret 결과를 차트 부착용 순수 스펙으로 변환. times = 캔들 epoch(초) 배열(오름차순). */
export function toChartSpec(doc: IndicatorDoc, result: InterpretResult, times: number[]): DslChartSpec {
  const lines: DslLineSpec[] = [];
  const priceLines: DslPriceLineSpec[] = [];
  const markers: DslMarkerSpec[] = [];
  const histograms: DslHistogramSpec[] = [];
  const areas: DslAreaSpec[] = [];
  const baselines: DslBaselineSpec[] = [];
  // bgcolor는 시간당 1색만 그릴 수 있음(히스토그램 트릭) — 같은 시간에 여러 조건이 겹치면 뒤 visual 승리
  const backgroundAt = new Map<number, DslBackgroundSpec>();

  doc.visuals.forEach((visual, index) => {
    if (visual.kind === 'line') {
      const style = visual.style ?? 'solid'; const opacity = visual.opacity ?? 1;
      lines.push({
        key: `line${index}:${visual.source}`,
        points: toPoints(times, result.values[visual.source]),
        color: visual.color,
        width: visual.width ?? 1,
        dashed: false,
        ...(style !== 'solid' ? { style } : {}),
        ...(opacity !== 1 ? { opacity } : {}),
      });
    } else if (visual.kind === 'band' || visual.kind === 'fill') {
      // 면 채움 폴백: 경계 점선 2개 (fillOpacity/opacity는 v1 렌더에서 미사용)
      const [upper, lower] = visual.kind === 'band' ? [visual.upper, visual.lower] : [visual.a, visual.b];
      lines.push({ key: `${visual.kind}${index}:${upper}`, points: toPoints(times, result.values[upper]), color: visual.color, width: 1, dashed: true });
      lines.push({ key: `${visual.kind}${index}:${lower}`, points: toPoints(times, result.values[lower]), color: visual.color, width: 1, dashed: true });
    } else if (visual.kind === 'hline') {
      priceLines.push({ price: visual.value, color: visual.color, ...(visual.label ? { label: visual.label } : {}) });
    } else if (visual.kind === 'marker') {
      result.flags[visual.condition].forEach((flag, i) => {
        if (flag === true) markers.push({ time: times[i] as UTCTimestamp, position: visual.position, shape: visual.shape, color: visual.color, ...(visual.label ? { text: visual.label } : {}) });
      });
    } else if (visual.kind === 'bgcolor') {
      result.flags[visual.condition].forEach((flag, i) => {
        if (flag === true) backgroundAt.set(times[i], { time: times[i] as UTCTimestamp, color: visual.color, opacity: visual.opacity ?? 0.12 });
      });
    } else if (visual.kind === 'histogram') {
      const base = visual.base ?? 0;
      histograms.push({
        key: `histogram${index}:${visual.source}`,
        base,
        points: result.values[visual.source].map((value, i) => value == null
          ? { time: times[i] as UTCTimestamp }
          : { time: times[i] as UTCTimestamp, value, color: value >= base ? visual.colorUp : visual.colorDown }),
      });
    } else if (visual.kind === 'area') {
      areas.push({ key: `area${index}:${visual.source}`, points: toPoints(times, result.values[visual.source]), color: visual.color, opacity: visual.opacity ?? 0.15 });
    } else {
      baselines.push({ key: `baseline${index}:${visual.source}`, points: toPoints(times, result.values[visual.source]), base: visual.base ?? 0, colorUp: visual.colorUp, colorDown: visual.colorDown });
    }
  });

  // v5는 마커 time 오름차순 필수 — 미정렬 시 마커가 통째로 사라짐(PriceChart 9b와 동일 방어)
  markers.sort((a, b) => (a.time as number) - (b.time as number));
  return {
    lines,
    priceLines,
    markers,
    backgrounds: [...backgroundAt.values()].sort((a, b) => (a.time as number) - (b.time as number)),
    ...(histograms.length ? { histograms } : {}),
    ...(areas.length ? { areas } : {}),
    ...(baselines.length ? { baselines } : {}),
  };
}

/** rgb()/hex 색에 불투명도 합성 — canvas는 color-mix를 못 읽어 rgba/hex8로 변환 */
export function withOpacity(color: string, opacity: number): string {
  const alpha = Math.round(Math.min(1, Math.max(0, opacity)) * 255);
  if (color.startsWith('rgb(')) return color.replace('rgb(', 'rgba(').replace(')', `, ${(alpha / 255).toFixed(3)})`);
  if (color[0] === '#' && color.length === 7) return color + alpha.toString(16).padStart(2, '0');
  return color;
}

type AttachOptions = {
  paneIndex: number; // 0=메인 오버레이, 2+=보조패널(PriceChart pane 배치 관례)
  resolveColor: (raw: string) => string; // 슬롯명/hex → canvas rgb (resolveCanvasColor(raw, C) 커링)
  markerAnchor?: ISeriesApi<'Candlestick'> | ISeriesApi<'Line'> | null; // 마커를 붙일 시리즈(메인 pane이면 캔들) — 없으면 지표 첫 라인
  scaleId?: string; // bgcolor 전용 오버레이 스케일 id(문서별 유니크 권장)
};

/** 스펙을 chart에 부착. 반환 시리즈는 chart.remove()로 일괄 정리되므로 개별 해제 불필요. */
export function attachDslIndicator(chart: IChartApi, spec: DslChartSpec, { paneIndex, resolveColor, markerAnchor, scaleId = 'dsl-bg' }: AttachOptions): ISeriesApi<'Line' | 'Histogram' | 'Area' | 'Baseline'>[] {
  const series: ISeriesApi<'Line' | 'Histogram' | 'Area' | 'Baseline'>[] = [];
  let firstLine: ISeriesApi<'Line'> | null = null;

  for (const line of spec.lines) {
    const style = line.style ?? (line.dashed ? 'dashed' : 'solid');
    const resolved = resolveColor(line.color);
    const s = chart.addSeries(LineSeries, { color: line.opacity == null || line.opacity === 1 ? resolved : withOpacity(resolved, line.opacity), lineWidth: line.width, lineStyle: style === 'solid' ? 0 : style === 'dotted' ? 1 : 2, priceLineVisible: false, lastValueVisible: false }, paneIndex);
    s.setData(line.points);
    firstLine = firstLine ?? s;
    series.push(s);
  }

  // hline은 시리즈에 붙는 priceLine — 라인이 하나도 없는 문서(마커·배경 전용)에선 앵커가 없어 생략
  if (firstLine) for (const pl of spec.priceLines) firstLine.createPriceLine({ price: pl.price, color: resolveColor(pl.color), lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: pl.label ?? '' });

  const anchor = markerAnchor ?? firstLine;
  if (spec.markers.length && anchor) {
    createSeriesMarkers(anchor, spec.markers.map((m) => ({ ...m, color: resolveColor(m.color) })) as SeriesMarker<UTCTimestamp>[]);
  }

  for (const histogram of spec.histograms ?? []) {
    const s = chart.addSeries(HistogramSeries, { base: histogram.base, priceLineVisible: false, lastValueVisible: false }, paneIndex);
    s.setData(histogram.points.map((point) => 'value' in point ? { ...point, color: histogram.opacity == null ? resolveColor(point.color) : withOpacity(resolveColor(point.color), histogram.opacity) } : point));
    series.push(s);
  }

  for (const area of spec.areas ?? []) {
    const color = resolveColor(area.color);
    const seriesOpacity = area.seriesOpacity ?? 1;
    const s = chart.addSeries(AreaSeries, { lineColor: seriesOpacity === 1 ? color : withOpacity(color, seriesOpacity), topColor: withOpacity(color, area.opacity * seriesOpacity), bottomColor: withOpacity(color, 0), priceLineVisible: false, lastValueVisible: false }, paneIndex);
    s.setData(area.points);
    series.push(s);
  }

  for (const baseline of spec.baselines ?? []) {
    const colorUp = resolveColor(baseline.colorUp); const colorDown = resolveColor(baseline.colorDown);
    const opacity = baseline.opacity ?? 1;
    const s = chart.addSeries(BaselineSeries, {
      baseValue: { type: 'price', price: baseline.base },
      topLineColor: opacity === 1 ? colorUp : withOpacity(colorUp, opacity),
      topFillColor1: withOpacity(colorUp, 0.28 * opacity),
      topFillColor2: withOpacity(colorUp, 0.05 * opacity),
      bottomLineColor: opacity === 1 ? colorDown : withOpacity(colorDown, opacity),
      bottomFillColor1: withOpacity(colorDown, 0.05 * opacity),
      bottomFillColor2: withOpacity(colorDown, 0.28 * opacity),
      priceLineVisible: false,
      lastValueVisible: false,
    }, paneIndex);
    s.setData(baseline.points);
    series.push(s);
  }

  // bgcolor: 전체 높이 히스토그램 트릭 — 별도 오버레이 스케일(margins 0)에 value 1 고정 봉을 세워 배경처럼 칠함
  if (spec.backgrounds.length) {
    const bg = chart.addSeries(HistogramSeries, { priceScaleId: scaleId, priceLineVisible: false, lastValueVisible: false, priceFormat: { type: 'volume' } }, paneIndex);
    bg.priceScale().applyOptions({ scaleMargins: { top: 0, bottom: 0 }, visible: false });
    bg.setData(spec.backgrounds.map((b) => ({ time: b.time, value: 1, color: withOpacity(resolveColor(b.color), b.opacity) })));
    series.push(bg);
  }
  return series;
}
